import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isNetworkFetchError = (error: unknown) =>
  error instanceof TypeError ||
  (error instanceof Error && error.message.toLowerCase().includes("failed to fetch"));

const getSupabaseAuthStorageKey = () => {
  const projectRef = new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split(".")[0];
  return `sb-${projectRef}-auth-token`;
};

const SESSION_BACKUP_KEY = "lovable-auth-session-backup";

const backupSession = (session: Session | null) => {
  try {
    if (session?.refresh_token && session?.access_token) {
      window.localStorage.setItem(SESSION_BACKUP_KEY, JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at,
      }));
    }
  } catch {}
};

const readBackup = (): { access_token: string; refresh_token: string; expires_at?: number } | null => {
  try {
    const raw = window.localStorage.getItem(SESSION_BACKUP_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearBackup = () => {
  try { window.localStorage.removeItem(SESSION_BACKUP_KEY); } catch {}
};

const passwordTokenEndpoints = () => {
  const directEndpoint = `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/token?grant_type=password`;
  return import.meta.env.DEV ? ["/__supabase-auth/token?grant_type=password", directEndpoint] : [directEndpoint];
};

const previewAuthPasswordSignIn = async (email: string, password: string): Promise<Session> => {
  let payload: Record<string, any> | null = null;
  let lastError: Error | null = null;

  for (const endpoint of passwordTokenEndpoints()) {
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          "content-type": "application/json;charset=UTF-8",
          "x-client-info": "password-auth-fallback",
        },
        body: JSON.stringify({ email, password, gotrue_meta_security: {} }),
      });

      const responsePayload = await response.json().catch(() => ({}));

      if (!response.ok) {
        lastError = new Error(responsePayload?.msg || responsePayload?.error_description || responsePayload?.error || "Sign in failed");
        continue;
      }

      payload = responsePayload;
      break;
    } catch (error) {
      lastError = error as Error;
    }
  }

  if (!payload) {
    throw lastError ?? new Error("Sign in failed");
  }

  const nextSession = {
    access_token: payload.access_token,
    expires_at: payload.expires_at,
    expires_in: payload.expires_in,
    refresh_token: payload.refresh_token,
    token_type: payload.token_type ?? "bearer",
    user: payload.user,
  } as Session;

  window.localStorage.setItem(getSupabaseAuthStorageKey(), JSON.stringify(nextSession));

  let setSessionError: Error | null = null;

  try {
    const { error } = await supabase.auth.setSession({
      access_token: nextSession.access_token,
      refresh_token: nextSession.refresh_token,
    });
    setSessionError = error as Error | null;
  } catch (error) {
    setSessionError = error as Error;
  }

  if (setSessionError && isNetworkFetchError(setSessionError)) {
    window.setTimeout(() => window.location.assign("/dashboard"), 0);
  } else if (setSessionError) {
    throw setSessionError;
  }

  return nextSession;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const intentionalSignOutRef = useRef(false);
  const lastRestoreAtRef = useRef(0);
  const restoringRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const loadingTimeout = window.setTimeout(() => {
      if (!isMounted) return;
      console.warn("Auth initialization timed out; continuing without session.");
      setLoading(false);
    }, 10000);

    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted) return;
      // Guard against transient refresh failures signing the user out.
      // Only clear session on an explicit user-initiated sign out.
      if (!nextSession && event === "SIGNED_OUT" && !intentionalSignOutRef.current) {
        console.warn("[auth] Ignoring unsolicited SIGNED_OUT (likely token refresh hiccup)");
        setLoading(false);
        window.clearTimeout(loadingTimeout);
        // Attempt silent restore from backup so the user stays signed in.
        const backup = readBackup();
        const now = Date.now();
        // Guard: one restore attempt at most every 60s, never concurrently.
        // Without this, each failed setSession emits another SIGNED_OUT and we
        // hammer the auth server until it returns "Request rate limit reached".
        if (backup?.refresh_token && !restoringRef.current && now - lastRestoreAtRef.current > 60_000) {
          restoringRef.current = true;
          lastRestoreAtRef.current = now;
          void supabase.auth
            .setSession({
              access_token: backup.access_token,
              refresh_token: backup.refresh_token,
            })
            .then(({ data, error }) => {
              // A dead/expired refresh token can never be restored — drop it so
              // we stop retrying and let the user sign in normally.
              if (error || !data?.session) clearBackup();
            })
            .catch(() => clearBackup())
            .finally(() => {
              restoringRef.current = false;
            });
        } else if (!backup?.refresh_token) {
          // Nothing to restore — treat as a real signed-out state.
          setSession(null);
          setUser(null);
        }
        return;
      }
      if (nextSession) {
        setSession(nextSession);
        setUser(nextSession.user ?? null);
        backupSession(nextSession);
      } else if (intentionalSignOutRef.current) {
        setSession(null);
        setUser(null);
        intentionalSignOutRef.current = false;
        clearBackup();
      }
      setLoading(false);
      window.clearTimeout(loadingTimeout);
    });

    // THEN check for existing session
    const initializeSession = async () => {
      try {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();

        if (error) throw error;
        if (!isMounted) return;

        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user ?? null);
          backupSession(currentSession);
        } else {
          // No live session — try to restore from our backup (handles refresh-token race).
          const backup = readBackup();
          if (backup?.refresh_token) {
            const { data, error: restoreError } = await supabase.auth.setSession({
              access_token: backup.access_token,
              refresh_token: backup.refresh_token,
            });
            if (!restoreError && data.session) {
              setSession(data.session);
              setUser(data.session.user ?? null);
              backupSession(data.session);
            } else {
              clearBackup();
              setSession(null);
              setUser(null);
            }
          } else {
            setSession(null);
            setUser(null);
          }
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to initialize auth session:", error);
        setSession(null);
        setUser(null);
      } finally {
        if (!isMounted) return;
        setLoading(false);
        window.clearTimeout(loadingTimeout);
      }
    };

    void initializeSession();

    return () => {
      isMounted = false;
      window.clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    
    // Create profile record after successful signup
    if (!error && data.user) {
      await supabase.from("profiles").upsert({
        user_id: data.user.id,
        full_name: fullName,
        email: email,
      }, { onConflict: "user_id" });
    }
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error && isNetworkFetchError(error)) {
        const nextSession = await previewAuthPasswordSignIn(email, password);
        setSession(nextSession);
        setUser(nextSession.user);
        return { error: null };
      }

      return { error: error as Error | null };
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        return { error: error as Error };
      }

      try {
        const nextSession = await previewAuthPasswordSignIn(email, password);
        setSession(nextSession);
        setUser(nextSession.user);
        return { error: null };
      } catch (fallbackError) {
        return { error: fallbackError as Error };
      }
    }
  };

  const signOut = async () => {
    intentionalSignOutRef.current = true;
    clearBackup();
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
